import { ArrowLeft, List, Plus, Wrench } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { EmptyState } from '@/components/ui/empty-state';
import { EquipmentTree } from '@/components/ui/equipment-tree';
import { Skeleton } from '@/components/ui/skeleton';
import { useEquipmentTree } from '@/features/equipment/queries';
import { useVessel } from '@/features/vessels/queries';
import { vesselDisplayName } from '@/features/vessels/types';
import { ApiError } from '@/lib/api';

function TreeSkeleton() {
  return (
    <div className="space-y-2">
      {Array.from({ length: 6 }).map((_, i) => (
        <div
          key={i}
          className="flex items-center gap-2"
          style={{ paddingLeft: `${(i % 3) * 1.25 + 0.5}rem` }}
        >
          <Skeleton className="h-5 w-5 rounded" />
          <Skeleton className="h-5 w-5 rounded" />
          <Skeleton className="h-4 w-12" />
          <Skeleton className="h-4 w-48" />
          <Skeleton className="ml-auto h-5 w-6 rounded" />
        </div>
      ))}
    </div>
  );
}

export function VesselEquipmentTreePage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const { data: vessel } = useVessel(id);
  const {
    data: tree,
    isLoading,
    isError,
    error,
    refetch,
  } = useEquipmentTree(id);

  const errorMessage = (() => {
    if (!isError) return null;
    if (error instanceof ApiError) {
      return t(error.key, {
        defaultValue: t('equipment.list.loadError'),
        ...(error.params ?? {}),
      });
    }
    return t('equipment.list.loadError');
  })();

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Button asChild variant="ghost" size="icon" aria-label={t('common.back')}>
            <Link to={id ? `/vessels/${id}` : '/vessels'}>
              <ArrowLeft aria-hidden />
            </Link>
          </Button>
          <div>
            <h1 className="text-2xl font-semibold">
              {vessel ? vesselDisplayName(vessel) : t('common.loading')}
            </h1>
            <p className="text-sm text-muted-foreground">
              {t('equipment.tree.title')}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button asChild variant="outline">
            <Link to={id ? `/equipment?vesselId=${id}` : '/equipment'}>
              <List aria-hidden />
              {t('equipment.tree.viewAsList')}
            </Link>
          </Button>
          <Button asChild>
            <Link to={id ? `/equipment/new?vesselId=${id}` : '/equipment/new'}>
              <Plus aria-hidden />
              {t('equipment.list.newButton')}
            </Link>
          </Button>
        </div>
      </header>

      {errorMessage && (
        <Alert variant="destructive">
          <AlertTitle>{t('equipment.list.loadError')}</AlertTitle>
          <AlertDescription className="space-y-2">
            <p>{errorMessage}</p>
            <Button variant="outline" size="sm" onClick={() => refetch()}>
              {t('common.retry')}
            </Button>
          </AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            {t('equipment.tree.hierarchyTitle')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading && <TreeSkeleton />}
          {!isLoading && !isError && tree && tree.length === 0 && (
            <EmptyState
              icon={Wrench}
              title={t('equipment.tree.empty')}
              description={t('equipment.tree.emptyHint')}
              action={
                <Button asChild size="sm">
                  <Link
                    to={id ? `/equipment/new?vesselId=${id}` : '/equipment/new'}
                  >
                    <Plus aria-hidden />
                    {t('equipment.list.newButton')}
                  </Link>
                </Button>
              }
            />
          )}
          {!isLoading && tree && tree.length > 0 && (
            <EquipmentTree nodes={tree} />
          )}
        </CardContent>
      </Card>
    </div>
  );
}
